import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthStepperFooter from 'src/features/auth/presentation/components/AuthStepperFooter/AuthStepperFooter';
import LineClampTypography from 'src/components/common/ui/LineClampTypography';
import { useAppUI } from 'src/context/AppUIProvider/useAppUI';
import ConditionalWrapper from 'src/components/ConditionalWrapper';
import { CenteredContentContainer, FlexContainer } from 'src/components/styles';
import { useCheckUserExists } from 'src/features/auth/presentation/hooks/useCheckUserExists';
import LoaderOverlay from 'src/components/Loader';
import useAuthMutations from 'src/features/auth/presentation/hooks/useAuthMutations';
import { useAuthenticationOtpMutations } from 'src/features/verification/presentation/hooks/useAuthenticationOtpMutations';
import { useVerificationToken } from 'src/features/auth/presentation/providers/VerificationTokenProvider';

export const WelcomeScreen = () => {
  const { isMobile, alertNotification } = useAppUI();
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { verificationToken, decodedUser } = useVerificationToken();
  const { userExists, isLoading } = useCheckUserExists(
    decodedUser?.phone_number
  );
  const { registerJwtMutation } = useAuthMutations();
  const { sendLoginOtpMutation } = useAuthenticationOtpMutations();

  const disableContinue =
    !acceptedTerms ||
    !verificationToken ||
    !decodedUser?.phone_number ||
    registerJwtMutation.isPending;

  const handleNewUserContinue = () => {
    if (disableContinue) return;

    registerJwtMutation.mutate(
      { token: verificationToken },
      {
        onSuccess: () => {
          navigate(`/specialized-stores/verify?token=${verificationToken}`);
        },
        onError: (error) => {
          console.error(error);
          alertNotification.error('Ocurrió un error al registrar el usuario');
        },
      }
    );
  };

  const handleExistingUserContinue = () => {
    if (disableContinue) return;

    sendLoginOtpMutation.mutate(
      {
        phone_number: decodedUser.phone_number,
        email: decodedUser.email,
        type: 'AUTHENTICATION',
      },
      {
        onSuccess: () => {
          navigate(`/specialized-stores/verify?token=${verificationToken}`);
        },
        onError: () =>
          alertNotification.error('Ocurrió un error al enviar el código.'),
      }
    );
  };

  const handleContinue = () => {
    if (!verificationToken || !decodedUser) {
      return;
    }

    if (userExists) {
      handleExistingUserContinue();
    } else {
      handleNewUserContinue();
    }
  };

  if (isLoading || !decodedUser) {
    return <LoaderOverlay />;
  }

  return (
    <Box>
      <LineClampTypography
        content="¡Bienvenido a Pari!"
        lines={1}
        typographyProps={{
          variant: isMobile ? 'h4' : 'h2',
          color: '#000',
          fontWeight: 'bold',
          textAlign: 'center',
          mb: 2,
        }}
        disableFulltextToggle
      />
      <Typography variant="body1" paragraph>
        Estamos emocionados de tenerte en nuestra comunidad. Para proteger tu
        cuenta y brindarte una experiencia segura, necesitamos verificar tu
        identidad.
      </Typography>
      <Typography variant="body1" paragraph>
        Recibirás un código de verificación en tu correo electrónico y número de
        teléfono registrados.
      </Typography>

      <ConditionalWrapper
        condition={isMobile}
        wrapper={(children) => (
          <Box position="fixed" bottom={0} left={0} right={0} px={2} pb={5}>
            {children}
          </Box>
        )}
      >
        <CenteredContentContainer pt={3} gap={{ md: 2, xs: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
              />
            }
            label={
              <FlexContainer gap={0.5} alignItems="center">
                <Typography color="gray">Acepto los</Typography>
                <a
                  href="https://www.pari.mx/terminos-y-condiciones"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Typography
                    sx={{ textDecoration: 'underline' }}
                    color="inherit"
                  >
                    Términos y Condiciones
                  </Typography>
                </a>
              </FlexContainer>
            }
          />
          <AuthStepperFooter
            nextButton={{
              label: 'Continuar',
              onClick: handleContinue,
              variant: 'contained',
              disabled: disableContinue,
            }}
          />
        </CenteredContentContainer>
      </ConditionalWrapper>
    </Box>
  );
};
